import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  PhoneIcon,
  UserGroupIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  UsersIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import RapportiServizioSection from "../components/RapportiServizioSection";

const DAY_IN_MS = 24 * 60 * 60 * 1000;
const SLOT_LABELS = {
  infrasettimanale: "Infrasettimanale",
  fine_settimana: "Fine settimana",
};

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }
  const date =
    value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateParam = (value) => {
  const normalized = normalizeDate(value);
  if (!normalized) {
    return "";
  }
  const tzOffset = normalized.getTimezoneOffset() * 60000;
  return new Date(normalized.getTime() - tzOffset).toISOString().split("T")[0];
};

const getDateKey = (value) => {
  const formatted = formatDateParam(value);
  if (formatted) {
    return formatted;
  }
  if (typeof value === "string") {
    const [datePart] = value.split("T");
    if (datePart) {
      return datePart;
    }
  }
  return "data_non_disponibile";
};

const IMieiTurni = () => {
  const { user, activeCongregazione } = useAuth();
  const isConduttore = user?.sesso === "M" && user?.service_leader;
  const [activeTab, setActiveTab] = useState("postazioni");
  const [turniPostazioni, setTurniPostazioni] = useState([]);
  const [adunanze, setAdunanze] = useState([]);
  const [pulizie, setPulizie] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    postazioni: true,
    adunanze: true,
    pulizie: true,
  });
  const [tabMessages, setTabMessages] = useState({
    postazioni: null,
    adunanze: null,
    pulizie: null,
  });
  const [expandedCards, setExpandedCards] = useState(new Set());
  const activeCongregazioneId = activeCongregazione?.id;
  const [turnDetails, setTurnDetails] = useState({
    open: false,
    loading: false,
    error: null,
    turns: [],
    selectedTurnId: null,
    meta: null,
  });

  useEffect(() => {
    if (!user?.id) return;

    let isMounted = true;

    const loadTurni = async () => {
      setLoadingStates({
        postazioni: true,
        adunanze: true,
        pulizie: true,
      });
      setTabMessages({
        postazioni: null,
        adunanze: null,
        pulizie: null,
      });
      if (activeCongregazioneId) {
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      const oggi = normalizeDate(new Date());
      const dataInizio = formatDateParam(oggi);
      const dataFine = formatDateParam(
        new Date((oggi?.getTime() || 0) + 90 * DAY_IN_MS)
      );

      const adunanzePromise = isConduttore
        ? api.get(`/adunanze-servizio/miei-turni?limit=50`)
        : Promise.resolve({ data: { assignments: [] }, skip: true });

      const results = await Promise.allSettled([
        api.get(
          `/turni/miei-turni?data_inizio=${dataInizio}&data_fine=${dataFine}`
        ),
        adunanzePromise,
        api.get(
          `/turni-pulizie/miei-turni?data_inizio=${dataInizio}&data_fine=${dataFine}`
        ),
      ]);

      if (!isMounted) {
        return;
      }

      const nextMessages = {
        postazioni: null,
        adunanze: null,
        pulizie: null,
      };

      const [turniRes, adunanzeRes, pulizieRes] = results;

      // Postazioni
      if (turniRes.status === "fulfilled") {
        const turniData = Array.isArray(turniRes.value.data)
          ? turniRes.value.data
          : [];
        const futureTurni = turniData.filter((turno) => {
          const turnoDate = normalizeDate(turno.data_turno);
          return turnoDate && oggi && turnoDate >= oggi;
        });

        setTurniPostazioni(futureTurni);
        if (futureTurni.length === 0) {
          nextMessages.postazioni = "Nessun turno futuro assegnato";
        }
      } else {
        const error = turniRes.reason;
        console.error("Errore nel caricamento turni:", error);
        if (error?.response?.status === 401) {
          toast.error("Sessione scaduta. Effettua nuovamente l'accesso.");
        } else {
          toast.error(
            error?.response?.data?.message ||
              "Errore nel caricamento dei turni"
          );
        }
        setTurniPostazioni([]);
        nextMessages.postazioni =
          error?.response?.data?.message || "Errore nel caricamento dei turni";
      }

      // Adunanze del servizio
      if (!isConduttore) {
        setAdunanze([]);
        nextMessages.adunanze =
          "Solo i conduttori vedono gli incarichi di adunanze del servizio.";
      } else if (adunanzeRes.status === "fulfilled") {
        const assignments = Array.isArray(adunanzeRes.value?.data?.assignments)
          ? adunanzeRes.value.data.assignments
          : [];
        const futureAdunanze = assignments
          .map((adunanza, idx) => ({
            ...adunanza,
            id:
              adunanza.adunanza_id && adunanza.data_occurrence
                ? `${adunanza.adunanza_id}-${adunanza.data_occurrence}`
                : `${adunanza.adunanza_id || "adunanza"}-${idx}`,
          }))
          .filter((adunanza) => {
            const adunanzaDate = normalizeDate(adunanza.data_occurrence);
            return adunanzaDate && oggi && adunanzaDate >= oggi;
          })
          .sort((a, b) => {
            const dateA = normalizeDate(a.data_occurrence);
            const dateB = normalizeDate(b.data_occurrence);
            return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
          });

        setAdunanze(futureAdunanze);
        if (futureAdunanze.length === 0) {
          nextMessages.adunanze =
            "Nessuna adunanza assegnata nel periodo selezionato.";
        }
      } else {
        const error = adunanzeRes.reason;
        const status = error?.response?.status;
        if (status !== 403) {
          toast.error(
            error?.response?.data?.message ||
              "Errore nel caricamento delle adunanze"
          );
        }
        setAdunanze([]);
        nextMessages.adunanze =
          status === 403
            ? "Solo i conduttori vedono gli incarichi di adunanze del servizio."
            : error?.response?.data?.message ||
              "Errore nel caricamento delle adunanze";
      }

      // Pulizie
      if (pulizieRes.status === "fulfilled") {
        const assignments = Array.isArray(pulizieRes.value?.data?.assignments)
          ? pulizieRes.value.data.assignments
          : [];
        const sortedAssignments = assignments
          .map((assignment, idx) => ({
            ...assignment,
            id: `${assignment.settimana || "settimana"}-${
              assignment.slot || idx
            }`,
          }))
          .sort((a, b) => {
            const dateA = normalizeDate(a.settimana);
            const dateB = normalizeDate(b.settimana);
            const order = { infrasettimanale: 0, fine_settimana: 1 };
            if (dateA && dateB && dateA.getTime() !== dateB.getTime()) {
              return dateA.getTime() - dateB.getTime();
            }
            return (order[a.slot] || 0) - (order[b.slot] || 0);
          });

        setPulizie(sortedAssignments);
        if (sortedAssignments.length === 0) {
          nextMessages.pulizie =
            "Nessun incarico di pulizie nel periodo selezionato.";
        }
      } else {
        const error = pulizieRes.reason;
        console.error("Errore nel caricamento turni pulizie:", error);
        toast.error(
          error?.response?.data?.message ||
            "Errore nel caricamento dei turni pulizie"
        );
        setPulizie([]);
        nextMessages.pulizie =
          error?.response?.data?.message ||
          "Errore nel caricamento dei turni pulizie";
      }

      setTabMessages(nextMessages);
      setLoadingStates({
        postazioni: false,
        adunanze: false,
        pulizie: false,
      });
    };

    loadTurni();

    return () => {
      isMounted = false;
    };
  }, [user?.id, activeCongregazioneId, isConduttore]);

  const toggleCard = (turnoId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(turnoId)) {
      newExpanded.delete(turnoId);
    } else {
      newExpanded.add(turnoId);
    }
    setExpandedCards(newExpanded);
  };

  const formatDate = (dateString) => {
    const date = normalizeDate(dateString);
    if (!date) {
      return "Data non disponibile";
    }
    const days = [
      "Domenica",
      "Lunedì",
      "Martedì",
      "Mercoledì",
      "Giovedì",
      "Venerdì",
      "Sabato",
    ];
    const months = [
      "Gennaio",
      "Febbraio",
      "Marzo",
      "Aprile",
      "Maggio",
      "Giugno",
      "Luglio",
      "Agosto",
      "Settembre",
      "Ottobre",
      "Novembre",
      "Dicembre",
    ];
    return `${days[date.getDay()]} ${date.getDate()} ${
      months[date.getMonth()]
    } ${date.getFullYear()}`;
  };

  const formatAdunanzaDate = (value) => {
    if (!value) {
      return "Data non disponibile";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "Data non disponibile";
    }
    const formatter = new Intl.DateTimeFormat("it-IT", {
      weekday: "long",
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const capitalize = (text) =>
      text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

    return formatter
      .formatToParts(date)
      .map((part) => {
        if (part.type === "weekday" || part.type === "month") {
          return capitalize(part.value);
        }
        return part.value;
      })
      .join("");
  };

  const capitalizeFirst = (word = "") =>
    word ? word.charAt(0).toUpperCase() + word.slice(1) : "";

  const formatWeekRange = (startIso, endIso) => {
    if (!startIso || !endIso) return "";
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return "";
    }
    const formatter = new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "long",
    });
    const capitalizeMonth = (str = "") => {
      if (!str) return "";
      const parts = str.split(" ");
      if (parts.length === 2) {
        const month = parts[1];
        parts[1] = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
        return parts.join(" ");
      }
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };
    return `${capitalizeMonth(formatter.format(start))} - ${capitalizeMonth(
      formatter.format(end)
    )}`;
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    // Rimuovi i secondi se presenti (formato HH:MM:SS -> HH:MM)
    return timeString.substring(0, 5);
  };

  const formatPhone = (phone) => {
    if (!phone) return "Non disponibile";
    // Formatta il numero italiano se possibile
    if (phone.length === 10 && phone.startsWith("3")) {
      return `${phone.substring(0, 3)} ${phone.substring(3, 6)} ${phone.substring(6, 8)} ${phone.substring(8)}`;
    }
    return phone;
  };

  const formatTimeShort = (timeString) => {
    if (!timeString) return "";
    return timeString.substring(0, 5);
  };

  const closeTurnDetails = () => {
    setTurnDetails({
      open: false,
      loading: false,
      error: null,
      turns: [],
      selectedTurnId: null,
      meta: null,
    });
  };

  const openTurnDetails = async (turno) => {
    if (!turno?.id) return;

    const startTime = formatTime(turno.orario?.inizio);
    const endTime = formatTime(turno.orario?.fine);
    const orarioLabel =
      [startTime, endTime].filter(Boolean).join(" - ") ||
      "Orario non indicato";

    setTurnDetails({
      open: true,
      loading: true,
      error: null,
      turns: [],
      selectedTurnId: turno.id,
      meta: {
        luogo: turno.postazione?.luogo,
        data: turno.data_turno,
        orario: orarioLabel,
      },
    });

    try {
      const detailRes = await api.get(`/turni/${turno.id}`);
      const detail = detailRes.data || {};

      const rawDate = detail.data_turno || turno.data_turno;
      const dateKey =
        typeof rawDate === "string"
          ? rawDate.split("T")[0]
          : formatDateParam(rawDate);

      const postazioneId =
        detail.postazione_id || detail.postazioneId || detail.postazione?.id;

      if (!postazioneId || !dateKey) {
        throw new Error("Impossibile recuperare la postazione o la data del turno");
      }

      const dayRes = await api.get(
        `/turni?postazione_id=${postazioneId}&data_inizio=${dateKey}&data_fine=${dateKey}`
      );

      const dayTurns = Array.isArray(dayRes.data) ? dayRes.data : [];
      const mapped = dayTurns
        .map((t) => ({
          id: t.id,
          orarioInizio: formatTimeShort(t.orario_inizio || t.orario?.inizio),
          orarioFine: formatTimeShort(t.orario_fine || t.orario?.fine),
          volontari: t.volontari_nomi
            ? t.volontari_nomi.split(", ").filter(Boolean)
            : Array.isArray(t.volontari)
            ? t.volontari
            : [],
          stato: t.stato,
          luogo: t.luogo,
        }))
        .sort((a, b) => (a.orarioInizio || "").localeCompare(b.orarioInizio || ""));

      setTurnDetails({
        open: true,
        loading: false,
        error: null,
        turns: mapped,
        selectedTurnId: detail.id || turno.id,
        meta: {
          luogo: detail.luogo || turno.postazione?.luogo,
          data: dateKey,
          orario: orarioLabel,
        },
      });
    } catch (err) {
      setTurnDetails((prev) => ({
        ...prev,
        loading: false,
        error:
          err.response?.data?.message ||
          err.message ||
          "Errore nel caricamento dei turni della giornata",
      }));
    }
  };

  // Raggruppa i turni per data
  const turniPerData = turniPostazioni.reduce((acc, turno) => {
    const dataKey = getDateKey(turno.data_turno);
    if (!acc[dataKey]) {
      acc[dataKey] = [];
    }
    acc[dataKey].push(turno);
    return acc;
  }, {});

  // Ordina le date
  const dateKeys = Object.keys(turniPerData).sort((a, b) => {
    const dateA = normalizeDate(a);
    const dateB = normalizeDate(b);
    if (!dateA || !dateB) {
      return a.localeCompare(b);
    }
    return dateA - dateB;
  });

  const puliziePerSettimana = useMemo(() => {
    const grouped = pulizie.reduce((acc, assignment, idx) => {
      const key = assignment.settimana || `settimana_${idx}`;
      if (!acc[key]) {
        acc[key] = {
          settimana: assignment.settimana,
          settimana_fine: assignment.settimana_fine || assignment.settimana,
          items: [],
        };
      }
      acc[key].items.push(assignment);
      return acc;
    }, {});

    const order = { infrasettimanale: 0, fine_settimana: 1 };

    return Object.values(grouped)
      .map((week) => ({
        ...week,
        items: (week.items || []).slice().sort((a, b) => {
          return (order[a.slot] || 0) - (order[b.slot] || 0);
        }),
      }))
      .sort((a, b) => {
        const dateA = normalizeDate(a.settimana);
        const dateB = normalizeDate(b.settimana);
        return (dateA?.getTime() || 0) - (dateB?.getTime() || 0);
      });
  }, [pulizie]);

  const tabOptions = useMemo(
    () => [
      {
        key: "postazioni",
        label: "Postazioni",
        icon: MapPinIcon,
        count: turniPostazioni.length,
      },
      {
        key: "adunanze",
        label: "Adunanze Servizio",
        icon: UsersIcon,
        count: adunanze.length,
      },
      {
        key: "pulizie",
        label: "Pulizie",
        icon: SparklesIcon,
        count: pulizie.length || puliziePerSettimana.length,
      },
    ],
    [turniPostazioni.length, adunanze.length, pulizie.length, puliziePerSettimana.length]
  );

  const renderLoadingSkeleton = (rows = 3) => (
    <div className="animate-pulse space-y-4">
      <div className="h-8 bg-gray-200 rounded w-1/2 mb-6"></div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
      ))}
    </div>
  );

  const renderPostazioni = () => {
    if (loadingStates.postazioni) {
      return renderLoadingSkeleton();
    }

    if (dateKeys.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">
            {tabMessages.postazioni || "Nessun turno futuro assegnato"}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            I tuoi turni assegnati appariranno qui non appena disponibili
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {dateKeys.map((data) => (
          <div key={data}>
            {/* Header Data */}
            <div className="mb-4">
              <div className="flex items-center">
                <CalendarIcon className="h-5 w-5 text-primary-600 mr-2" />
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {formatDate(data)}
                </h2>
                <span className="ml-3 text-sm text-gray-500">
                  ({turniPerData[data].length}{" "}
                  {turniPerData[data].length === 1 ? "turno" : "turni"})
                </span>
              </div>
            </div>

            {/* Cards Turni */}
            <div className="space-y-4">
              {turniPerData[data].map((turno) => {
                const isExpanded = expandedCards.has(turno.id);
                const compagni = Array.isArray(turno.compagni)
                  ? turno.compagni
                  : [];
                const orarioInizio = formatTime(turno.orario?.inizio);
                const orarioFine = formatTime(turno.orario?.fine);
                const orarioLabel =
                  [orarioInizio, orarioFine].filter(Boolean).join(" - ") ||
                  "Orario non indicato";
                const luogo = turno.postazione?.luogo || "Postazione";
                const indirizzo = turno.postazione?.indirizzo;

                return (
                  <div
                    key={turno.id}
                    className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                  >
                    <span
                      className="accent-bar top-4 bottom-4"
                      aria-hidden="true"
                    />
                    {/* Card Header - sempre visibile */}
                    <div
                      className="p-4 pl-6 sm:p-6 sm:pl-8 cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => toggleCard(turno.id)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          {/* Postazione */}
                          <div className="flex items-start mb-3">
                            <MapPinIcon className="h-5 w-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                                {luogo}
                              </h3>
                              {indirizzo && (
                                <p className="text-sm text-gray-600 truncate mt-1">
                                  {indirizzo}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Data e Orario */}
                          <div className="flex flex-wrap items-center gap-4 mt-3">
                            <div className="flex items-center text-sm text-gray-700">
                              <ClockIcon className="h-4 w-4 text-gray-500 mr-1.5" />
                              <span className="font-medium">
                                {orarioLabel}
                              </span>
                            </div>

                            {/* Compagni count */}
                            <div className="flex items-center text-sm text-gray-700">
                              <UserGroupIcon className="h-4 w-4 text-gray-500 mr-1.5" />
                              <span>
                                {compagni.length > 0
                                  ? `${compagni.length} ${compagni.length === 1 ? "compagno" : "compagni"}`
                                  : "Nessun compagno"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Azioni header */}
                        <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                          <div
                            className={`transition-all duration-200 ease-out transform ${
                              isExpanded
                                ? "opacity-100 translate-y-0 scale-100"
                                : "opacity-0 -translate-y-1 scale-95 pointer-events-none"
                            }`}
                          >
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                openTurnDetails(turno);
                              }}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-primary-200 text-xs font-bold text-primary-700 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
                              title="Vedi chi è prima e dopo di te in questa giornata"
                            >
                              ?
                            </button>
                          </div>
                          <button
                            className="p-1 text-gray-400 hover:text-gray-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleCard(turno.id);
                            }}
                            aria-label={isExpanded ? "Chiudi dettagli turno" : "Apri dettagli turno"}
                          >
                            {isExpanded ? (
                              <ChevronUpIcon className="h-5 w-5" />
                            ) : (
                              <ChevronDownIcon className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Card Body - espandibile */}
                    {isExpanded && compagni.length > 0 && (
                      <div className="border-t border-gray-200 p-4 pl-6 sm:p-6 sm:pl-8 bg-gray-50">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                          <UserGroupIcon className="h-4 w-4 mr-2" />
                          Compagni di Turno
                        </h4>
                        <div className="space-y-3">
                          {compagni.map((compagno) => (
                            <div
                              key={compagno.id}
                              className="bg-white rounded-lg p-3 sm:p-4 border border-gray-200"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 text-sm sm:text-base">
                                    {compagno.nome_completo}
                                  </p>
                                  {compagno.sesso && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      {compagno.sesso === "M" ? "Uomo" : "Donna"}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="mt-3 space-y-2">
                                {compagno.telefono && (
                                  <a
                                    href={`tel:${compagno.telefono.replace(/\s/g, "")}`}
                                    className="flex items-center text-sm text-primary-600 hover:text-primary-700 transition-colors"
                                  >
                                    <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                    <span className="break-all">{formatPhone(compagno.telefono)}</span>
                                  </a>
                                )}
                                {!compagno.telefono && (
                                  <p className="text-xs text-gray-500">
                                    Contatti non disponibili
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderAdunanze = () => {
    if (loadingStates.adunanze) {
      return renderLoadingSkeleton(2);
    }

    if (adunanze.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">
            {tabMessages.adunanze ||
              "Nessuna adunanza assegnata nel periodo selezionato"}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            Le adunanze del servizio che condurrai appariranno qui.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {adunanze.map((adunanza, index) => {
          const luogo = adunanza.luogo || adunanza.titolo || "Adunanza";
          const indirizzo = adunanza.indirizzo?.trim();
          const orario = adunanza.ora_inizio
            ? adunanza.ora_inizio.substring(0, 5)
            : "";
          const statoLabel = capitalizeFirst(adunanza.stato || "programmata");

          return (
            <div
              key={adunanza.id || `${adunanza.adunanza_id}_${index}`}
              className="relative bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="space-y-2">
                    <p className="text-xs uppercase text-gray-500 flex items-center gap-1">
                      <UsersIcon className="h-4 w-4 text-primary-600" />
                      Adunanze del servizio
                    </p>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {luogo}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {formatAdunanzaDate(adunanza.data_occurrence)}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
                      {orario && (
                        <span className="inline-flex items-center gap-1.5">
                          <ClockIcon className="h-4 w-4 text-gray-500" />
                          {orario}
                        </span>
                      )}
                      <span className="inline-flex items-center gap-1.5">
                        <MapPinIcon className="h-4 w-4 text-gray-500" />
                        {indirizzo || luogo}
                      </span>
                    </div>
                    {adunanza.note && (
                      <p className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 leading-snug">
                        {adunanza.note}
                      </p>
                    )}
                  </div>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-800 border border-blue-100 self-start">
                    {statoLabel}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderPulizie = () => {
    if (loadingStates.pulizie) {
      return renderLoadingSkeleton(2);
    }

    if (puliziePerSettimana.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 text-lg font-medium">
            {tabMessages.pulizie ||
              "Nessun incarico di pulizie nel periodo selezionato"}
          </p>
          <p className="text-gray-500 text-sm mt-2">
            I turni del tuo gruppo appariranno qui.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {puliziePerSettimana.map((week, idx) => {
          const weekRange =
            formatWeekRange(week.settimana, week.settimana_fine) ||
            formatDate(week.settimana) ||
            week.settimana;
          return (
            <div
              key={week.settimana || idx}
              className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 overflow-hidden"
            >
              <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-lg">
                    🧹
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Turno pulizie
                    </p>
                    <p className="text-sm text-gray-700 leading-tight">
                      {weekRange}
                    </p>
                  </div>
                </div>
                <span className="self-start sm:self-auto inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                  Settimana
                </span>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-3 mt-4">
                {week.items.map((item, i) => {
                  const groupNames = (item.groups || [])
                    .map((g) => g.group_nome)
                    .filter(Boolean);
                  const slotLabel =
                    SLOT_LABELS[item.slot] ||
                    capitalizeFirst(item.slot || "Slot");
                  const slotShort = item.slot?.startsWith("fine") ? "FS" : "INF";
                  return (
                    <div
                      key={`${item.slot || "slot"}_${i}`}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-gray-800"
                    >
                      <div className="flex items-center gap-3">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
                          {slotShort}
                        </span>
                        <div className="leading-snug">
                          <p className="font-medium text-gray-900">
                            {slotLabel}
                          </p>
                          <p className="text-gray-700">
                            {groupNames.length > 0
                              ? `Gruppo: ${groupNames.join(", ")}`
                              : "Gruppo non indicato"}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl mx-auto w-full min-w-0 overflow-x-hidden">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
          I Miei Turni
        </h1>
        <p className="text-gray-600 mt-1 text-sm sm:text-base">
          Consulta rapidamente i turni alle postazioni, le adunanze del servizio e gli incarichi di pulizia del tuo gruppo.
        </p>
        <div className="accent-line mt-3" />
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-2 sm:p-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {tabOptions.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-3 w-full px-3 py-2 rounded-md border text-left transition ${
                  isActive
                    ? "border-primary-200 bg-primary-50 text-primary-800 shadow-sm"
                    : "border-gray-200 bg-white text-gray-800 hover:border-primary-200 hover:text-primary-800"
                }`}
                aria-pressed={isActive}
              >
                <span
                  className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    isActive ? "bg-white text-primary-700" : "bg-gray-100 text-gray-600"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{tab.label}</p>
                  <p className="text-xs text-gray-500">
                    {tab.key === "postazioni" && "Turni alle postazioni"}
                    {tab.key === "adunanze" && "Incarichi da conduttore"}
                    {tab.key === "pulizie" && "Turni gruppo pulizie"}
                  </p>
                </div>
                <span
                  className={`text-xs font-semibold px-2 py-1 rounded-full ${
                    isActive
                      ? "bg-primary-100 text-primary-800"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {activeTab === "postazioni" && renderPostazioni()}
      {activeTab === "adunanze" && renderAdunanze()}
      {activeTab === "pulizie" && renderPulizie()}

      {activeTab === "postazioni" && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Suggerimento:</strong> Clicca su un turno per vedere i dettagli dei tuoi compagni e i loro contatti.
          </p>
        </div>
      )}

      {/* Modal turni della giornata */}
      {activeTab === "postazioni" && turnDetails.open && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xl">
            <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
              <div>
                <p className="text-xs uppercase text-gray-500">Turni della giornata</p>
                <h3 className="text-lg font-semibold text-gray-900">
                  {turnDetails.meta?.luogo || "Postazione"}
                </h3>
                {turnDetails.meta?.data && (
                  <p className="text-xs text-gray-500">
                    {formatDate(turnDetails.meta.data)}
                  </p>
                )}
              </div>
              <button
                onClick={closeTurnDetails}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Chiudi dettagli turni"
              >
                ✕
              </button>
            </div>
            <div className="p-5 space-y-4">
              {turnDetails.loading ? (
                <div className="flex items-center justify-center py-6 space-x-2 text-primary-700">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                  <span>Caricamento turni della giornata...</span>
                </div>
              ) : turnDetails.error ? (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                  {turnDetails.error}
                </div>
              ) : turnDetails.turns.length > 0 ? (
                <>
                  <p className="text-sm text-gray-600">
                    Vedi chi è prima o dopo di te alla stessa postazione nella giornata.
                  </p>
                  {(() => {
                    const selectedIndex = turnDetails.turns.findIndex(
                      (t) => t.id === turnDetails.selectedTurnId
                    );
                    return turnDetails.turns.map((t, idx) => {
                      const isCurrent = idx === selectedIndex;
                      const label = isCurrent
                        ? "Il tuo turno"
                        : idx < selectedIndex
                        ? "Prima"
                        : "Dopo";
                      return (
                        <div
                          key={`${t.id}_${idx}`}
                          className={`border rounded-lg p-3 ${
                            isCurrent
                              ? "border-primary-200 bg-primary-50"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">
                                {t.orarioInizio && t.orarioFine
                                  ? `${t.orarioInizio} - ${t.orarioFine}`
                                  : t.orarioInizio || t.orarioFine || "Orario non indicato"}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {t.volontari && t.volontari.length > 0
                                  ? t.volontari.join(", ")
                                  : "Proclamatori non indicati"}
                              </p>
                            </div>
                            <span
                              className={`text-xs font-medium px-2 py-1 rounded-full ${
                                isCurrent
                                  ? "bg-primary-100 text-primary-800"
                                  : "bg-gray-200 text-gray-700"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </>
              ) : (
                <p className="text-sm text-gray-600">
                  Nessun turno trovato per questa giornata alla postazione.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <RapportiServizioSection mode="personal" />
    </div>
  );
};

export default IMieiTurni;
